import { cn } from '@/lib/cn';

/**
 * Backlog #20's other half of "skeletons and one toast/error surface" — a
 * pulsing placeholder shaped like the content that's about to arrive,
 * replacing the "Loading…" text block `LoadingState` (States.tsx) renders
 * today. Deliberately not a replacement for `LoadingState`: a bare text
 * block is still right for a small, low-stakes region (a form section, an
 * inline count); `Skeleton` composes into row/card/tile shapes for a list
 * or dashboard region that has a *known* structure worth previewing, which
 * is why `SkeletonRows`/`SkeletonCard` below exist as compositions rather
 * than asking every call site to hand-stack bare `<Skeleton>` divs.
 *
 * `bg-muted` is the same token `Badge`'s neutral variant and `DataTable`'s
 * hover state use — already theme-aware, so this needs no dark-mode-specific
 * handling of its own.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} />;
}

/** A DataTable-shaped loading preview: a header bar plus `rows` body bars. */
export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="w-full overflow-hidden rounded-xl border border-border">
      <div className="flex gap-3 border-b border-border bg-card p-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-3 p-3">
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton key={c} className={cn('h-3 flex-1', c === 0 && 'max-w-16')} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A StatCard/SectionCard-shaped preview — a title bar, a big value bar, a caption bar. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-4', className)}>
      <Skeleton className="mb-3 h-3 w-24" />
      <Skeleton className="mb-2 h-7 w-16" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}
